"use client";

import { IResource } from "@gettactic/api";
import { LoadingSpinner } from "@gettactic/components/src/loadings/LoadingSpinner/LoadingSpinner";
import { useLoadingToast } from "@gettactic/helpers/src/hooks/toasts/useLoadingToast";
import { useInvalidateZones } from "@gettactic/hooks";
import { MapCanvasValue } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext";
import type { MapSaveOptions } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import { MapBuilder } from "@gettactic/map-builder/src/MapBuilder";
import { ResourceEditingOptions } from "@gettactic/map-builder/src/types";
import {
  getSpotsWithoutResourceId,
  getSpotsWithResourceId
} from "@gettactic/map-builder/src/utils/spots";
import { prepareRegionsForSave } from "@gettactic/map-builder/src/utils/zones";
import { MapEditor as SvgMapEditor } from "@gettactic/map-editor";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { createPortal } from "react-dom";
import { useFloorEditorCanvasData } from "@/features/map/hooks/useFloorEditorCanvasData";
import { useMap } from "@/features/map/MapProvider";
import { applySavedFloorMapData } from "@/features/map/utils/applySavedFloorMapData";
import { api } from "@/lib/api/api";
import { useSvgMapEditorEnabled } from "@/lib/hooks/useSvgMapEditorEnabled";

export interface QuickDeskCreatorProps {
  floor: IResource;
  officeId: string;
  /** Shown as a read-only reference marker on the canvas (e.g. where the
   *  workplace request being resolved was originally pinned), so the desk
   *  can be placed in roughly the right spot. */
  referencePoint?: { x: number; y: number };
  /** Called once the newly placed desk has been saved, with the resource id
   *  the server assigned it. */
  onDeskCreated: (resourceId: string) => void;
  onExit: () => void;
}

/**
 * A light version of the map builder for a single task: adding one desk to
 * a floor without leaving the flow that needs it (e.g. assigning a seat
 * that doesn't exist on the map yet). Admin-only — gate rendering behind
 * the same `authenticatedUser.permissions.settings` check the full map
 * editor uses.
 *
 * Reuses the real map builder canvas via `MapBuilder`'s `restrictedMode` —
 * so placement precision, snap-to-grid alignment, and the per-shape
 * toolbox (color, duplicate, arrange) all work exactly as they do in the
 * full editor — but trims the toolbars down to selection, pan, and the
 * resource creator/style tools, and drops the floor switcher, resources
 * panel, icon upload and composite types this flow doesn't need. Saving
 * requires exactly one new spot to have been placed since the editor
 * opened, and the floor must already have a map — setting one up from
 * scratch is a full Map Builder task.
 */
export const QuickDeskCreator: React.FC<QuickDeskCreatorProps> = ({
  floor,
  officeId,
  referencePoint,
  onDeskCreated,
  onExit
}) => {
  const queryClient = useQueryClient();
  const invalidateZones = useInvalidateZones();
  const { updateFloorMapData } = useMap();
  // Same props either way; the SVG editor is flag-gated per user.
  const EditorComponent = useSvgMapEditorEnabled() ? SvgMapEditor : MapBuilder;
  const toast = useLoadingToast({ loadingMessage: "Saving desk..." });
  const [editingResources, setEditingResources] =
    React.useState<ResourceEditingOptions>({
      isOpen: false,
      resourceIds: []
    });

  const { officeResources, isResourcesLoading, floorData, interactiveZones } =
    useFloorEditorCanvasData(officeId, floor);

  // Baseline of any spots already missing a resource_id when the editor
  // opened, so a save only treats a *newly placed* spot as "the new desk" —
  // never a pre-existing data gap that predates this session.
  const initialSpotIdsWithoutResourceId = React.useMemo(() => {
    if (!floorData) return new Set<string>();
    return new Set(
      getSpotsWithoutResourceId(floorData.shapes).map((spot) => spot.id)
    );
  }, [floorData]);

  const hasNoMapYet = !isResourcesLoading && !floorData;

  // This tool only adds to an existing map — setting one up is a full Map
  // Builder task. Bail out as soon as we know there's nothing to edit,
  // rather than mounting a canvas with nothing on it.
  React.useEffect(() => {
    if (!hasNoMapYet) return;
    toast.error("This floor has no map yet — set it up in Map Builder first");
    onExit();
  }, [hasNoMapYet]);

  const onMapSave = async (
    data: MapCanvasValue,
    saveOptions: MapSaveOptions
  ) => {
    data.shapes = prepareRegionsForSave(data.shapes);

    const newSpots = getSpotsWithoutResourceId(data.shapes).filter(
      (spot) => !initialSpotIdsWithoutResourceId.has(spot.id)
    );

    if (newSpots.length === 0) {
      toast.error("Place a desk on the map before saving");
      return;
    }
    if (newSpots.length > 1) {
      toast.error("Add just one desk at a time here");
      return;
    }

    const mapPostBody: { map_data: string; map_active_from?: string } = {
      map_data: JSON.stringify(data)
    };
    if (saveOptions.mode === "activate-now") {
      mapPostBody.map_active_from = new Date().toISOString();
    } else if (saveOptions.mode === "schedule" && saveOptions.activeFrom) {
      mapPostBody.map_active_from = saveOptions.activeFrom;
    }

    const response = await api.client.organizations.resourceMapPost(
      floor.id,
      mapPostBody
    );
    const savedMapData = response.result?.map_data;
    if (!savedMapData) {
      toast.error("Could not save the desk");
      return;
    }

    const savedShapes = (JSON.parse(savedMapData) as MapCanvasValue).shapes;
    const savedSpot = getSpotsWithResourceId(savedShapes).find(
      (spot) => spot.id === newSpots[0].id
    );
    if (!savedSpot?.resource_id) {
      toast.error("Desk saved, but its id couldn't be resolved");
      return;
    }

    // Unlike the full editor, there's no in-progress draft to protect here —
    // refetch the resources list so the new desk is immediately selectable.
    applySavedFloorMapData({
      queryClient,
      floorId: floor.id,
      savedMapData,
      sentShapes: data.shapes,
      skipResourcesListRefetch: false
    });
    // The cache patch above doesn't reach MapProvider's own `floor` state —
    // without this the finder remounts on the pre-save map_data once this
    // detour closes (same as the full editor's NewMapEditor `onSaved`).
    updateFloorMapData(savedMapData);
    // The new desk changes which space holds it: refresh the zone tree,
    // health and floor-space queries as the full editor does on save.
    await invalidateZones();

    toast.success("Desk created");
    onDeskCreated(savedSpot.resource_id);
  };

  if (isResourcesLoading || hasNoMapYet || !floorData) {
    return createPortal(
      <div className="fixed inset-0 z-overlay flex items-center justify-center bg-background-primary">
        <LoadingSpinner />
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-overlay flex bg-background-primary">
      <div className="min-w-0 flex-1">
        <EditorComponent
          key={floor.id}
          onSave={onMapSave}
          value={floorData}
          resources={officeResources}
          interactiveZones={interactiveZones}
          resourceId={floor.id}
          title={`Add a desk — ${floor.name}`}
          setEditingResources={setEditingResources}
          editingResources={editingResources}
          restrictedMode
          initialActiveTool="spot"
          guidePoint={referencePoint}
          onExit={onExit}
        />
      </div>
    </div>,
    document.body
  );
};
